/**
 * Decision API client. Reads the audit_log projection.
 *
 *   GET /api/decisions             — most recent across all pairs
 *   GET /api/decisions?pairId=...  — filtered to one pair (pair-detail)
 */

import type { DecisionRow } from "@pfh/shared";
import { api } from "../api";

export type { DecisionRow } from "@pfh/shared";

export async function listDecisions(pairId?: string): Promise<DecisionRow[]> {
  const path = pairId
    ? `/api/decisions?pairId=${encodeURIComponent(pairId)}`
    : "/api/decisions";
  const res = await api.get<{ decisions: DecisionRow[] }>(path);
  return res.decisions;
}

/**
 * Full audit row — includes raw_prompt, raw_response, usage counts,
 * rag_chunk_ids, etc. Shown in the decision-detail dialog only; never
 * fetched in bulk.
 */
export interface DecisionDetail {
  id: number;
  request_id: string | null;
  tier: "haiku" | "sonnet";
  pair_id: string;
  symbol: string | null;
  account_id: string | null;
  firm_id: string;
  firm_rules_version: number | null;
  firm_rulebook_hash: string | null;
  requested_action: string | null;
  claude_action: string;
  compliance_status: string | null;
  violation_detail: string | null;
  rules_checked: string | null;
  reason: string;
  confidence: number | null;
  rag_chunk_ids: string | null;
  raw_prompt: string | null;
  raw_response: string | null;
  usage_input: number | null;
  usage_cache_read: number | null;
  usage_cache_write: number | null;
  usage_output: number | null;
  cost_usd: number | null;
  executed: number;
  created_at: number;
}

export async function getDecision(id: number | string): Promise<DecisionDetail> {
  const res = await api.get<{ decision: DecisionDetail }>(
    `/api/decisions/${encodeURIComponent(String(id))}`,
  );
  return res.decision;
}
